import {
  ComboDetailPayload,
  DiscoveryProduct,
  DiscoveryPromotion,
} from '@teko/discovery';
import { useServices, useTenantConfig } from 'contexts';
import { convertProductDetail, getNewVariant } from 'helpers';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { ComboItem, ComboProduct } from 'types';
import {
  useDiscoveryProduct,
  useRecommendationComboDetail,
} from './useDiscovery';
import { TProductVariant } from '@teko/ecommerce';

export const useDetailCombo = (
  type: ComboItem['type'],
  payload: ComboDetailPayload,
) => {
  const { getProcessEnv } = useTenantConfig();
  const calculatePriceByCart =
    getProcessEnv('CALCULATE_PRICE_BY_CART') === 'true';
  const [products, setProducts] = useState<ComboProduct[]>([]);
  const [payloadCombo, setPayloadCombo] = useState<ComboDetailPayload>(payload);
  const [comboPromotion, setComboPromotion] = useState<DiscoveryPromotion>();
  const [supplierRetailPrice, setSupplierRetailPrice] = useState<number>();
  const [latestPrice, setLatestPrice] = useState<number>();
  const skus = (payloadCombo?.comboLineItems || []).map((el) => el.sku);
  const { terminal, location } = useTenantConfig();
  const { discoveryService } = useServices();
  const fetcherListProduct = (_: any): Promise<DiscoveryProduct[]> => {
    return Promise.all(
      skus.map((sku) =>
        discoveryService
          .getProduct(
            { sku, terminalCode: terminal, location },
            calculatePriceByCart,
          )
          .then((res) => res?.result?.product),
      ),
    );
  };

  const { data: dataAutogenCombo, error: errorAutogen } = useSWR<
    DiscoveryProduct[]
  >(
    type === 'autogen' && skus?.length
      ? [`${discoveryService.getBaseUrl}/list-product`, JSON.stringify(skus)]
      : null,
    fetcherListProduct,
    { revalidateOnFocus: false },
  );

  const {
    result: { combo: dataDynamicCombo },
    isLoading: isLoadingDynamicCombo,
  } = useRecommendationComboDetail(payloadCombo, type === 'dynamic');

  const updatePrice = (newProducts: ComboProduct[]) => {
    setSupplierRetailPrice(
      newProducts.reduce((acc, curr) => {
        return acc + (curr.quantity || 0) * curr.supplierRetailPrice;
      }, 0),
    );
    setLatestPrice(
      newProducts.reduce((acc, curr) => {
        return acc + (curr.quantity || 0) * curr.latestPrice;
      }, 0),
    );
  };

  const onChangeQuantity = (sku: string, value: number) => {
    if (type === 'autogen') {
      const newProducts = products.map((el) => {
        if (el.sku === sku) {
          return {
            ...el,
            quantity: value,
          };
        }
        return el;
      });
      setProducts(newProducts);
      updatePrice(newProducts);
    }
  };

  const onChangeAutogenProduct = (index: number, newProduct: ComboProduct) => {
    const newProducts = products.map((el, idx) => {
      if (index === idx) return newProduct;
      return el;
    });
    setProducts(newProducts);
    setPayloadCombo({
      comboLineItems: newProducts.map((prod) => ({
        sku: prod.sku,
        quantity: prod.quantity,
        selectPromotionId: prod.selectionPromotionId,
      })),
    });
    updatePrice(newProducts);
  };

  const onChangeDynamicProduct = (newPayload: ComboDetailPayload) => {
    setPayloadCombo(newPayload);
  };

  const convertProductsAutogenCombo = () => {
    if (!dataAutogenCombo) return null;
    const newProducts = dataAutogenCombo.map((prod) => {
      const sku = prod.productInfo.sku;
      const quantity =
        payloadCombo.comboLineItems.find((line) => line.sku === sku)
          ?.quantity || 1;
      const convertedProduct = convertProductDetail(prod);
      const selectionPromotion = convertedProduct?.selectionPromotions?.[0];
      let gifts = [];
      const vouchers = [];
      (prod.defaultPromotions || []).forEach((el) => {
        if (!!el?.benefit?.gifts?.length) {
          gifts = [...gifts, ...el.benefit.gifts];
        }
        if (el?.benefit?.voucher?.name) {
          vouchers.push(el.benefit.voucher);
        }
      });
      if (selectionPromotion) {
        if (!!selectionPromotion?.benefit?.gifts?.length) {
          gifts = [...gifts, ...selectionPromotion.benefit.gifts];
        }
        if (selectionPromotion?.benefit?.voucher?.name) {
          vouchers.push(selectionPromotion.benefit.voucher);
        }
      }

      return {
        sku,
        quantity,
        image: prod.productInfo.imageUrl,
        name: prod.productInfo.name,
        selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
        supplierRetailPrice:
          Number(prod?.prices?.[0]?.supplierRetailPrice) || 0,
        latestPrice: Number(prod?.prices?.[0]?.latestPrice) || 0,
        selectionPromotionId: selectionPromotion?.id,
        defaultPromotions: convertedProduct?.defaultPromotions || [],
        selectionPromotion,
        selectionPromotions: convertedProduct?.selectionPromotions || [],
        allowSelectPromotion: convertedProduct?.selectionPromotions?.length > 0,
        totalAvailable: prod.totalAvailable,
        gifts,
        vouchers,
      } as ComboProduct;
    });
    setProducts(newProducts);
    updatePrice(newProducts);
  };

  const convertProductsDynamicCombo = () => {
    if (!dataDynamicCombo) return null;

    const newProducts = dataDynamicCombo.products.map((comboItem) => {
      const sku = comboItem.product.productInfo.sku;
      const quantity = comboItem.quantity;
      const convertedProduct = convertProductDetail(comboItem.product);
      let gifts = [];
      const vouchers = [];
      (comboItem.product.promotions || []).forEach((el) => {
        if (!!el?.benefit?.gifts?.length) {
          gifts = [...gifts, ...el.benefit.gifts];
        }
        if (el?.benefit?.voucher?.name) {
          vouchers.push(el.benefit.voucher);
        }
      });
      return {
        sku,
        quantity,
        image: comboItem.product.productInfo.imageUrl,
        name: comboItem.product.productInfo.name,
        selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
        supplierRetailPrice:
          Number(comboItem.product?.prices?.[0]?.supplierRetailPrice) || 0,
        latestPrice: Number(comboItem.product?.prices?.[0]?.latestPrice) || 0,
        defaultPromotions: comboItem.product.promotions || [],
        selectionPromotions: [],
        allowSelectPromotion: false,
        totalAvailable: comboItem.product.totalAvailable,
        gifts,
        vouchers,
      } as ComboProduct;
    });
    setComboPromotion({
      ...dataDynamicCombo.promotion,
      id: dataDynamicCombo.id,
    });
    setProducts(newProducts);
    setSupplierRetailPrice(Number(dataDynamicCombo.supplierRetailPrice) || 0);
    setLatestPrice(Number(dataDynamicCombo.latestPrice) || 0);
  };

  const onFinish = (
    callback: (
      comboType: ComboItem['type'],
      newPayloadCombo: ComboDetailPayload,
    ) => any,
  ) => {
    callback(type, payloadCombo);
  };

  const convertProducts = () => {
    if (type === 'autogen') return convertProductsAutogenCombo();
    else return convertProductsDynamicCombo();
  };

  useEffect(() => {
    setPayloadCombo(payload);
  }, [JSON.stringify(payload)]);

  useEffect(() => {
    convertProducts();
  }, [JSON.stringify(dataAutogenCombo), JSON.stringify(dataDynamicCombo)]);

  return {
    payloadCombo,
    products,
    latestPrice,
    supplierRetailPrice,
    onChangeQuantity,
    comboPromotion,
    onChangeAutogenProduct,
    onChangeDynamicProduct,
    onFinish,
    loadingCombo:
      type === 'autogen'
        ? !errorAutogen && !dataAutogenCombo
        : isLoadingDynamicCombo,
  };
};

export const useAutogenPrices = (
  sku: string,
  supplierRetailPrice: number,
  selectPromotionId?: number,
) => {
  const { terminal, getProcessEnv } = useTenantConfig();
  const calculatePriceByCart =
    getProcessEnv('CALCULATE_PRICE_BY_CART') === 'true';
  const { cartService } = useServices();
  const { data: latestPrice } = useSWR<number>(
    calculatePriceByCart && !!sku && !!terminal
      ? [`cart_service/calculate_price`, sku, terminal, selectPromotionId]
      : null,
    async (
      _: string,
      sku: string,
      terminal: string,
      selectPromotionId: number,
    ): Promise<number> => {
      return await cartService.calculatePrice(
        { terminal },
        { sku, selectPromotionId },
      );
    },
    {
      revalidateOnFocus: false,
    },
  );

  const {
    data: {
      saveWithoutSelectionPromotion,
      priceWithoutSelectionPromotion,
    } = {},
  } = useSWR<{
    saveWithoutSelectionPromotion?: number;
    priceWithoutSelectionPromotion?: number;
  }>(
    calculatePriceByCart && !!sku && !!terminal
      ? [
          `cart_service/calculate_save_without_selection_promotion`,
          sku,
          terminal,
        ]
      : null,
    async (
      _: string,
      sku: string,
      terminal: string,
    ): Promise<{
      saveWithoutSelectionPromotion?: number;
      priceWithoutSelectionPromotion?: number;
    }> => {
      const priceWithoutSelectionPromotion = await cartService.calculatePrice(
        { terminal },
        { sku, selectPromotionId: null },
      );
      if (
        !isNaN(+supplierRetailPrice) &&
        !isNaN(+priceWithoutSelectionPromotion)
      )
        return {
          saveWithoutSelectionPromotion:
            +supplierRetailPrice - priceWithoutSelectionPromotion,
          priceWithoutSelectionPromotion,
        };
    },
    {
      revalidateOnFocus: false,
    },
  );

  return {
    latestPrice,
    saveWithoutSelectionPromotion,
    priceWithoutSelectionPromotion,
  };
};

export const useAutogenSelectedProduct = (
  index: number,
  prod: ComboProduct,
) => {
  const { getProcessEnv } = useTenantConfig();
  const calculatePriceByCart =
    getProcessEnv('CALCULATE_PRICE_BY_CART') === 'true';
  const [skuVariant, setSkuVariant] = useState(prod.sku);
  const [selectionPromotionId, setSelectionPromotionId] = useState(
    prod.selectionPromotionId,
  );
  const [quantity, setQuantity] = useState(prod.quantity);
  const [convertProduct, setConvertProduct] = useState<ComboProduct>();
  const {
    result: { product },
    isLoading,
  }: { result: { product: DiscoveryProduct }; isLoading: boolean } =
    useDiscoveryProduct(skuVariant, calculatePriceByCart);
  const {
    latestPrice,
    priceWithoutSelectionPromotion,
    saveWithoutSelectionPromotion,
  } = useAutogenPrices(
    prod?.sku,
    prod?.supplierRetailPrice,
    selectionPromotionId,
  );

  const onChangeVariant = (variant: TProductVariant, index: number) => {
    const newSku = getNewVariant(product, variant, index);
    if (!!newSku) {
      setSkuVariant(newSku);
    }
  };

  useEffect(() => {
    if (!product) return;
    const convertedProduct = convertProductDetail(product);
    const selectionPromotion = (
      convertedProduct?.selectionPromotions || []
    ).find((el) => el.id === selectionPromotionId);
    let gifts = [];
    const vouchers = [];
    (product.defaultPromotions || []).forEach((el) => {
      if (!!el?.benefit?.gifts?.length) {
        gifts = [...gifts, ...el.benefit.gifts];
      }
      if (el?.benefit?.voucher?.name) {
        vouchers.push(el.benefit.voucher);
      }
    });
    const newProduct = {
      sku: skuVariant,
      quantity,
      image: product.productInfo.imageUrl,
      name: product.productInfo.name,
      selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
      supplierRetailPrice:
        Number(product?.prices?.[0]?.supplierRetailPrice) || 0,
      latestPrice: calculatePriceByCart
        ? latestPrice
        : Number(selectionPromotion?.price?.sellPrice) ||
          convertedProduct?.price?.priceWithoutSelectionPromotion ||
          Number(product?.prices?.[0]?.latestPrice) ||
          0,
      selectionPromotionId,
      defaultPromotions: convertedProduct?.defaultPromotions || [],
      selectionPromotion,
      selectionPromotions: convertedProduct?.selectionPromotions || [],
      allowSelectPromotion: true,
      totalAvailable: product.totalAvailable,
      gifts,
      vouchers,
      saveWithoutSelectionPromotion: calculatePriceByCart
        ? saveWithoutSelectionPromotion
        : convertedProduct.saveWithoutSelectionPromotion,
      productConfigurations: convertedProduct.productGroup.configurations,
      priceWithoutSelectionPromotion: calculatePriceByCart
        ? priceWithoutSelectionPromotion
        : convertedProduct?.price?.priceWithoutSelectionPromotion,
    };
    setConvertProduct(newProduct);
  }, [
    JSON.stringify(product),
    selectionPromotionId,
    quantity,
    latestPrice,
    saveWithoutSelectionPromotion,
    priceWithoutSelectionPromotion,
  ]);

  const onFinish = (callback: (idx: number, newProd: ComboProduct) => any) => {
    callback(index, convertProduct);
  };

  return {
    product: convertProduct,
    onChangeVariant,
    onChangeQuantity: setQuantity,
    onChangeSelectionPromotionId: setSelectionPromotionId,
    onFinish,
    loading: isLoading,
    selectionPromotionId,
  };
};

export const useDynamicSelectedProduct = (
  restSkus: string[],
  payload: ComboDetailPayload,
) => {
  const [payloadCombo, setPayloadCombo] = useState(payload);
  const {
    result: { combo: dataDynamicCombo },
    isLoading: isLoadingDynamicCombo,
  } = useRecommendationComboDetail(payloadCombo, true);
  const [convertProduct, setConvertProduct] = useState<ComboProduct>();

  const onChangeVariant = (variant: TProductVariant, index: number) => {
    const product = dataDynamicCombo.products.find(
      (el) => !restSkus.includes(el.product.productInfo.sku),
    )?.product;
    if (!product) return;
    const newSku = getNewVariant(product, variant, index);
    if (!!newSku) {
      const newPayloadCombo = { ...payloadCombo };
      newPayloadCombo.comboLineItems = payloadCombo.comboLineItems.map(
        (lineItem) => {
          if (lineItem.sku === product.productInfo.sku)
            return {
              sku: newSku,
              quantity: lineItem.quantity,
            };
          return lineItem;
        },
      );
      setPayloadCombo(newPayloadCombo);
    }
  };

  const onFinish = (callback: (newPayload: ComboDetailPayload) => any) => {
    callback(payloadCombo);
  };

  useEffect(() => {
    if (!dataDynamicCombo) return null;
    const comboProduct = dataDynamicCombo.products.find(
      (el) => !restSkus.includes(el.product.productInfo.sku),
    );
    if (!comboProduct) return null;
    const product = comboProduct.product;
    const convertedProduct = convertProductDetail(product);
    let gifts = [];
    const vouchers = [];
    (product.defaultPromotions || []).forEach((el) => {
      if (!!el?.benefit?.gifts?.length) {
        gifts = [...gifts, ...el.benefit.gifts];
      }
      if (el?.benefit?.voucher?.name) {
        vouchers.push(el.benefit.voucher);
      }
    });
    const supplierRetailPrice =
      Number(product?.prices?.[0]?.supplierRetailPrice) || 0;
    const latestPrice = Number(product?.prices?.[0]?.latestPrice) || 0;
    const newProduct = {
      sku: product.productInfo.sku,
      quantity: comboProduct.quantity,
      image: product.productInfo.imageUrl,
      name: product.productInfo.name,
      selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
      supplierRetailPrice,
      latestPrice,
      defaultPromotions: product?.promotions || [],
      selectionPromotions: [],
      allowSelectPromotion: false,
      totalAvailable: product.totalAvailable,
      gifts,
      vouchers,
      saveWithoutSelectionPromotion: supplierRetailPrice - latestPrice,
      productConfigurations: convertedProduct.productGroup.configurations,
    } as ComboProduct;
    setConvertProduct(newProduct);
  }, [JSON.stringify(dataDynamicCombo), JSON.stringify(restSkus)]);

  return {
    product: convertProduct,
    onChangeVariant,
    onFinish,
    loading: isLoadingDynamicCombo,
  };
};
